/* TasteCart — shared cart utilities + global web components.
   Loaded once via <script type="module"> from sections/taste-foundation.liquid
   (module scripts with the same src evaluate once). No external libraries.
   Integrates with Horizon 4.x via Shopify standard events (@shopify/events,
   mapped to the storefront CDN by the importmap in snippets/scripts.liquid). */

import { CartLinesUpdateEvent } from '@shopify/events';

const state = { cart: null, listeners: new Set() };

export function peso(cents) {
  return '₱' + Math.round(cents / 100).toLocaleString('en-US');
}

/* Tier math — mirrors snippets/taste-tier-price.liquid. Cents in, cents out.
   Discounted tiers round to the nearest ₱10 (=1000 cents); base tier never rounds. */
export function r10(cents) { return Math.round(cents / 1000) * 1000; }
export function tierUnit(base, qty) {
  if (qty >= 12) return r10(base * 0.85);
  if (qty >= 6) return r10(base * 0.90);
  if (qty >= 4) return r10(base * 0.95);
  return base;
}
export function tiers(base) {
  return [
    { min: 1, max: 3, price: base, pct: 0 },
    { min: 4, max: 5, price: r10(base * 0.95), pct: 5 },
    { min: 6, max: 11, price: r10(base * 0.90), pct: 10 },
    { min: 12, max: null, price: r10(base * 0.85), pct: 15 },
  ];
}

export async function fetchCart() {
  const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  state.cart = await r.json();
  emit();
  return state.cart;
}
function emit() {
  state.listeners.forEach((fn) => { try { fn(state.cart); } catch (e) { console.warn('[taste-cart]', e); } });
  document.dispatchEvent(new CustomEvent('taste:cart', { detail: state.cart }));
}
export function onCart(fn) {
  state.listeners.add(fn);
  if (state.cart) fn(state.cart);
  return () => state.listeners.delete(fn);
}

/* Add one or many items: [{ id: <variantId>, quantity: n }, ...].
   Mirrors Horizon's product-form.js bundle add: dispatch CartLinesUpdateEvent
   BEFORE the fetch (with a deferred promise), POST /cart/add.js with the
   cart-items-component section ids, then resolve the promise with the fresh
   cart — so Horizon's cart icon, drawer contents and auto-open react natively. */
export async function addItems(items, { open = true, message } = {}) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
      detail: { source: 'taste-cart' },
    })
  );

  let data = {};
  let ok = false;
  try {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    });
    data = await r.json().catch(() => ({}));
    ok = r.ok && !data.status;
  } catch (err) {
    deferred.reject(err);
    toast('Could not add to cart', true);
    throw err;
  }

  const cart = await fetchCart();
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      didError: !ok,
      sections: data.sections,
      items: cart.items,
      source: 'taste-cart',
      itemCount: cart.item_count,
    },
  });

  if (!ok) {
    const desc = data.description || data.message || 'Could not add to cart';
    toast(desc, true);
    throw new Error(desc);
  }
  if (message) toast(message);
  if (open) openDrawer();
  return cart;
}

/* Open Horizon's cart drawer: <theme-drawer id="cart-drawer"> exposes open()
   (assets/theme-drawer.js). Falls back to the cart page when the drawer isn't
   rendered (settings.cart_type != 'drawer' or on /cart). */
export function openDrawer() {
  const d = document.getElementById('cart-drawer');
  if (!d) { window.location.href = '/cart'; return; }
  if (typeof d.open === 'function') { d.open(); return; }
  customElements.whenDefined('theme-drawer').then(() => {
    if (typeof d.open === 'function') d.open();
  });
}

/* Toast — bottom-center dark pill, matches design (pop-in .3s, 2.2s auto-dismiss). */
let toastEl, toastTimer;
export function toast(msg, isError = false) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.setAttribute('role', 'status');
    toastEl.style.cssText =
      'position:fixed;left:50%;bottom:26px;transform:translateX(-50%) scale(.9);z-index:200;' +
      'background:#2a1a12;color:#f7f0e6;font:700 14px/1 "Hanken Grotesk",sans-serif;' +
      'padding:13px 22px;border-radius:30px;box-shadow:0 12px 34px rgba(28,16,9,.35);' +
      'opacity:0;transition:opacity .3s,transform .3s;pointer-events:none;max-width:90vw;';
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = (isError ? '⚠ ' : '✓ ') + msg;
  toastEl.style.background = isError ? '#b0492f' : '#2a1a12';
  requestAnimationFrame(() => { toastEl.style.opacity = '1'; toastEl.style.transform = 'translateX(-50%) scale(1)'; });
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toastEl.style.opacity = '0'; toastEl.style.transform = 'translateX(-50%) scale(.9)'; }, 2200);
}

export const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---- <taste-shipping-progress> — rendered by snippets/taste-shipping-progress.liquid ---- */
if (!customElements.get('taste-shipping-progress')) {
  customElements.define('taste-shipping-progress', class extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.dataset.threshold, 10) || 300000; // cents
      this.off = onCart((cart) => this.update(cart));
    }
    disconnectedCallback() { if (this.off) this.off(); }
    update(cart) {
      const total = cart ? cart.total_price : 0;
      const count = cart ? cart.item_count : 0;
      const unlocked = total >= this.threshold;
      const pct = Math.min(100, (total / this.threshold) * 100);
      const remaining = Math.max(0, this.threshold - total);
      const msg = this.querySelector('[data-msg]');
      const fill = this.querySelector('[data-fill]');
      const wrap = this.querySelector('[data-wrap]');
      if (msg) {
        msg.textContent = unlocked
          ? (this.dataset.unlockedText || "🎉 You've unlocked FREE delivery!")
          : count
            ? (this.dataset.partialText || "You're {amount} away from FREE delivery").replace('{amount}', peso(remaining))
            : (this.dataset.emptyText || 'Spend {amount} for FREE delivery').replace('{amount}', peso(this.threshold));
        msg.style.color = unlocked ? '#3a5a28' : '#7a5a26';
      }
      if (fill) {
        fill.style.width = pct + '%';
        fill.style.background = unlocked ? '#4f7a3a' : 'linear-gradient(90deg,#e0a23e,#b9762a)';
      }
      if (wrap) wrap.style.background = unlocked ? '#e6f0dd' : '#f6ecd8';
    }
  });
}

/* ---- <taste-qty-add> — stepper + add button (product cards, quick view) ---- */
if (!customElements.get('taste-qty-add')) {
  customElements.define('taste-qty-add', class extends HTMLElement {
    connectedCallback() {
      this.qty = 1;
      this.qEl = this.querySelector('[data-qty]');
      this.addEventListener('click', (e) => {
        const b = e.target.closest('button');
        if (!b || !this.contains(b)) return;
        if (b.hasAttribute('data-dec')) { e.preventDefault(); this.set(this.qty - 1); }
        else if (b.hasAttribute('data-inc')) { e.preventDefault(); this.set(this.qty + 1); }
        else if (b.hasAttribute('data-add')) { e.preventDefault(); this.add(b); }
      });
    }
    set(n) { this.qty = Math.max(1, n); if (this.qEl) this.qEl.textContent = this.qty; }
    async add(btn) {
      const id = parseInt(this.dataset.variant, 10);
      if (!id) { toast('Product not available yet', true); return; }
      btn.disabled = true;
      const old = btn.textContent;
      btn.textContent = '…';
      try {
        await addItems([{ id, quantity: this.qty }], { message: this.qty + '× ' + (this.dataset.title || 'Item') + ' added' });
        this.set(1);
      } catch (_) { /* toast already shown */ }
      btn.disabled = false;
      btn.textContent = old;
    }
  });
}

/* ---- <taste-multi-add> — kits, "shop this recipe", bundles ---- */
if (!customElements.get('taste-multi-add')) {
  customElements.define('taste-multi-add', class extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-add]') || this;
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        let items = [];
        try { items = JSON.parse(this.dataset.items || '[]'); } catch (_) {}
        items = items.filter((i) => i.id);
        if (!items.length) { toast('Those products are not in the store yet', true); return; }
        const old = btn.textContent;
        btn.disabled = true; btn.textContent = 'Adding…';
        try { await addItems(items, { message: this.dataset.message || 'Added to cart' }); } catch (_) {}
        btn.disabled = false; btn.textContent = old;
      });
    }
  });
}

/* ---- <taste-rail> — horizontal scroller with arrow buttons ---- */
if (!customElements.get('taste-rail')) {
  customElements.define('taste-rail', class extends HTMLElement {
    connectedCallback() {
      const rail = this.querySelector('[data-rail]');
      if (!rail) return;
      this.querySelectorAll('[data-left],[data-right]').forEach((b) =>
        b.addEventListener('click', () =>
          rail.scrollBy({ left: b.hasAttribute('data-left') ? -320 : 320, behavior: reducedMotion() ? 'auto' : 'smooth' })));
    }
  });
}

/* Refresh our cart state whenever Horizon (or any app speaking standard events)
   mutates the cart. Events fire at request start; `event.promise` settles with
   the post-mutation cart. Our own dispatches are skipped (fetchCart already ran). */
['shopify:cart:lines-update', 'shopify:cart:note-update', 'shopify:cart:discount-update'].forEach((ev) => {
  document.addEventListener(ev, (e) => {
    if (e.detail && e.detail.source === 'taste-cart') return;
    if (e.promise) e.promise.then(() => fetchCart()).catch(() => {});
    else fetchCart();
  });
});
fetchCart();

window.TasteCart = { peso, r10, tierUnit, tiers, fetchCart, onCart, addItems, openDrawer, toast, reducedMotion, get cart() { return state.cart; } };
document.dispatchEvent(new CustomEvent('taste:ready'));
